import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import "../../../../../../src/css/Calendar.css";
import "react-datepicker/dist/react-datepicker.css";
import translateFile from '../../../../../config/TranslateFile.jsx'

const DatePickerComponent = ({
  name,
  label,
  value,
  onChange,
  maxDate,
  minDate, // Default to January 1st, 2017 if not provided
  errorMessage = {},
  required = false,
  disabled = false,
  showYearDropdown = true,
  showMonthDropdown = true,
  isClearable = true, // Allows clearing (default behavior)
  preventClear = false, // NEW PROP: If true, prevents clearing
  onBlur,
  onKeyDown,
  showTimeSelect = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);
  const currentLang = translateFile();

  const dateInputRef = useRef(null); // Add ref for the current date picker input
  useEffect(() => {
    setInputValue(value ? format(value, "MM/dd/yyyy") : "");
  }, [value]);


  const handleDateChange = (selectedDate) => {
    if (selectedDate) {
      onChange(selectedDate);
    } else if (!preventClear) {
      onChange(null);
      if (dateInputRef.current) {
        dateInputRef.current.toggleCalendar(); // Open the calendar
      }
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("text").trim();
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

    if (dateRegex.test(pasteData)) {
      event.target.value = pasteData;
      onChange(new Date(pasteData));
    }
  };

  const handleKeyDown = (event, name) => {
    if (onKeyDown) { onKeyDown(event, name); }
    if (event.key === "Enter") {
      const currentDate = new Date(inputValue ? inputValue : "");
      if (currentDate)
        onChange(currentDate, name);
    }
    const regex = /^[0-9/]$/; // Allow numbers and "/"
    if (
      !regex.test(event.key) &&
      event.key !== "Backspace" &&
      event.key !== "Delete" &&
      !(event.ctrlKey && event.key === "v")
    ) {
      event.preventDefault();
    }
    // Auto-insert '/' when typing
    const input = event.target.value;
    if (event.key !== "Backspace" && event.key !== "Delete") {
      if (input?.length === 2 || input?.length === 5) {
        event.target.value = input + "/";
      }
    }
  };

  return (
    <div className="datepicker-container">
      {label && (
        <label className="col-md-3 w-100 form-label inmate-label">
          {label}
          {required && <span className="text-danger"> *</span>}
        </label>
      )}
      <div>
        <DatePicker
          ref={dateInputRef}
          name={name}
          disabled={disabled}
          selected={value ? format(value, "MM/dd/yyyy") : ""}
          onChange={handleDateChange}
          onBlur={onBlur}
          dateFormat={showTimeSelect ? "MM/dd/yyyy hh:mm aa" : "MM/dd/yyyy"}
          placeholderText="MM/DD/YYYY"
          isClearable={value ? true : false}
          showYearDropdown={showYearDropdown}
          scrollableYearDropdown
          showMonthDropdown={showMonthDropdown}
          scrollableMonthYearDropdown
          maxDate={maxDate}
          minDate={minDate}
          autoComplete="off"
          disabledKeyboardNavigation
          onKeyDown={handleKeyDown}
          //onKeyDown={onKeyDown}
          onCalendarOpen={() => {
            setIsYearOpen(false);
            setIsMonthOpen(false);
          }}
          customInput={
            <input
              className={`form-control fs-5 fw-normal ${errorMessage[name] ? "border-danger" : ""
                }`}
              value={inputValue ? format(inputValue, "MM/dd/yyyy") : ""}
              readOnly
              onKeyDown={handleKeyDown}
              //onKeyDown={onKeyDown}
              maxLength={10}
              placeholder="Select a date"
              onPaste={handlePaste}
              onCopy={(e) => e.preventDefault()} // Restrict copy
              onCut={(e) => e.preventDefault()} // Restrict cut
              autoComplete="off"
              onContextMenu={(e) => e.preventDefault()} // Disable right-click
            />
          }
          renderCustomHeader={({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
          }) => {
            const currentYear = new Date(date).getFullYear();
            const currentMonth = new Date(date).getMonth();

            const months = currentLang?.Months;

            // Dynamically get min and max values from props
            const minMonth = minDate ? new Date(minDate).getMonth() : 0;
            const maxMonth = maxDate ? new Date(maxDate).getMonth() : 11;
            const minYear = minDate
              ? new Date(minDate).getFullYear()
              : currentYear - 125;
            const maxYear = maxDate
              ? new Date(maxDate).getFullYear()
              : currentYear + 125;

            // Filter months based on the selected year
            const allowedMonths = months.filter((_, index) => {
              if (currentYear === minYear && currentYear === maxYear) {
                return index >= minMonth && index <= maxMonth;
              }
              if (currentYear === minYear) {
                return index >= minMonth;
              }
              if (currentYear === maxYear) {
                return index <= maxMonth;
              }
              return true;
            });

            // Fix if current month is not in allowedMonths
            const selectedMonthName = months[currentMonth];
            if (!allowedMonths.includes(selectedMonthName)) {
              const firstAllowedMonthName = allowedMonths[0];
              const newMonthIndex = months.indexOf(firstAllowedMonthName);
              changeMonth(newMonthIndex);
            }
            const years = Array.from(
              { length: maxYear - minYear + 1 },
              (_, i) => minYear + i
            );

            return (
              <div className="react-datepicker__header__dropdown react-datepicker__header__dropdown--scroll">
                <div className="custom-header-wrapper">
                  <button
                    className="arrow-icon"
                    onClick={decreaseMonth}
                    disabled={
                      currentMonth === minMonth && currentYear === minYear
                    }
                  >
                    <span style={{ fontSize: "20px", fontWeight: 800 }}>
                      {currentMonth === minMonth && currentYear === minYear
                        ? ""
                        : "<"}
                    </span>
                  </button>

                  {/* Custom Month Dropdown */}
                  <div className="custom-dropdown month-dropdown">
                    <div
                      className="dropdown-toggle"
                      onClick={() => {
                        setIsMonthOpen(!isMonthOpen);
                        setIsYearOpen(false);
                      }}
                    >
                      {months[currentMonth]}
                      <span className="arrow-icon-dropdown">
                        {isMonthOpen ? "▲" : "▼"}
                      </span>
                    </div>
                    {isMonthOpen && (
                      <div className="dropdown-menu1 scrollable">
                        {allowedMonths.map((month, index) => {
                          const isSelected =
                            months.indexOf(month) === currentMonth;
                          return (
                            <div
                              key={index}
                              ref={(el) => {
                                if (isSelected && el) {
                                  setTimeout(
                                    () =>
                                      el.scrollIntoView({ block: "nearest" }),
                                    0
                                  );
                                }
                              }}
                              className={`dropdown-item ${isSelected ? "selected" : ""
                                }`}
                              onClick={() => {
                                changeMonth(months.indexOf(month));
                                setIsMonthOpen(false);
                              }}
                            >
                              {month}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Custom Year Dropdown */}
                  <div className="custom-dropdown year-dropdown">
                    <div
                      className="dropdown-toggle"
                      onClick={() => {
                        setIsYearOpen(!isYearOpen);
                        setIsMonthOpen(false);
                      }}
                    >
                      {currentYear}
                      <span className="arrow-icon-dropdown">
                        {isYearOpen ? "▲" : "▼"}
                      </span>
                    </div>
                    {isYearOpen && (
                      <div className="dropdown-menu1 scrollable">
                        {years.map((year) => {
                          const isSelected = year === currentYear;
                          return (
                            <div
                              key={year}
                              ref={(el) => {
                                if (isSelected && el) {
                                  setTimeout(
                                    () =>
                                      el.scrollIntoView({ block: "nearest" }),
                                    0
                                  );
                                }
                              }}
                              className={`dropdown-item ${isSelected ? "selected" : ""
                                }`}
                              onClick={() => {
                                changeYear(year);
                                setIsYearOpen(false);
                              }}
                            >
                              {year}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Month Navigation Buttons */}
                  <button
                    className="arrow-icon"
                    onClick={increaseMonth}
                    disabled={
                      currentMonth === maxMonth && currentYear === maxYear
                    }
                  >
                    <span style={{ fontSize: "20px", fontWeight: 800 }}>
                      {currentMonth === maxMonth && currentYear === maxYear
                        ? ""
                        : ">"}
                    </span>
                  </button>
                </div>
              </div>
            );
          }}
          portalId="root" // This ensures it renders at the highest level in the DOM
          popperPlacement="top-start" // Adjust as needed
        />
        {errorMessage && Object.keys(errorMessage).length > 0 && (
         <div className="text-danger fw-700 fs-6 mt-1">
            {errorMessage[name]}
          </div>  
        )}
      </div>
    </div>
  );
};

export default DatePickerComponent;